import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  PhoneCall, 
  Check, 
  ArrowRight,
  ArrowLeft,
  Loader2,
  Server,
  Key,
  Building2,
  Webhook,
  Copy,
  ExternalLink,
  Cloud
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE_URL } from '../utils/api';
const API = API_BASE_URL;

const STEPS = [
  { id: 1, title: 'PBX Provider', icon: Cloud },
  { id: 2, title: 'SIP Credentials', icon: PhoneCall },
  { id: 3, title: 'API Integration', icon: Key },
  { id: 4, title: 'Company Details', icon: Building2 },
  { id: 5, title: 'Webhook Setup', icon: Webhook },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [copied, setCopied] = useState({ url: false, secret: false });

  // Form state
  const [form, setForm] = useState({
    // Step 1: PBX Provider
    pbx_provider: 'hero-wholesale',
    // Step 2: SIP Credentials
    sip_username: '',
    sip_password: '',
    account_number: '',
    // Step 3: API Integration
    api_base_url: 'https://admin1.connect-us.co.nz',
    api_username: '',
    api_password: '',
    // Step 4: Company Details
    company_name: '',
    contacts_url: ''
  });

  // Webhook data (provided by backend)
  const [webhookData, setWebhookData] = useState({
    url: '',
    secret: ''
  });

  // Fetch company data on mount
  useEffect(() => {
    if (!token) {
      toast.error('Invalid onboarding link');
      navigate('/subscribe');
      return;
    }

    fetchCompanyData();
  }, [token]);

  const fetchCompanyData = async () => {
    try {
      const res = await axios.get(`${API}/api/onboarding/${token}`);
      setCompanyData(res.data);
      setForm(prev => ({
        ...prev,
        company_name: res.data.company_name || ''
      }));
      setWebhookData({
        url: res.data.webhook_url || '',
        secret: res.data.webhook_secret || ''
      });
    } catch (error) {
      toast.error('Invalid or expired onboarding link');
      navigate('/subscribe');
    }
  };

  const handleNext = async () => {
    // Validate current step
    if (currentStep === 2) {
      if (!form.sip_username || !form.sip_password || !form.account_number) {
        toast.error('Please fill in all SIP credentials');
        return;
      }
    }
    if (currentStep === 3) {
      if (!form.api_username || !form.api_password) {
        toast.error('Please fill in API credentials');
        return;
      }
    }
    if (currentStep === 4) {
      if (!form.company_name) {
        toast.error('Please enter your company name');
        return;
      }
    }

    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Submit all onboarding data
      await axios.post(`${API}/api/onboarding/${token}/complete`, {
        pbx_provider: form.pbx_provider,
        sip_username: form.sip_username,
        sip_password: form.sip_password,
        account_number: form.account_number,
        api_base_url: form.api_base_url,
        api_username: form.api_username,
        api_password: form.api_password,
        company_name: form.company_name,
        contacts_url: form.contacts_url
      });

      toast.success('Setup complete! Redirecting to login...');
      
      // Redirect to login after short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    await navigator.clipboard.writeText(text);
    setCopied({ ...copied, [type]: true });
    setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
    toast.success('Copied to clipboard');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Choose Your PBX Provider</h2>
              <p className="text-slate-400">Select the cloud PBX service you're using</p>
            </div>

            <div className="grid gap-4">
              <label
                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  form.pbx_provider === 'hero-wholesale'
                    ? 'border-cyan-500 bg-cyan-500/10'
                    : 'border-slate-700 hover:border-slate-600'
                }`}
              >
                <input
                  type="radio"
                  name="provider"
                  value="hero-wholesale"
                  checked={form.pbx_provider === 'hero-wholesale'}
                  onChange={(e) => setForm({ ...form, pbx_provider: e.target.value })}
                  className="sr-only"
                />
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                  <Cloud className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-white">Hero Cloud PBX</div>
                  <div className="text-sm text-slate-400">Connect-US wholesale platform (securevoip.nz)</div>
                </div>
                {form.pbx_provider === 'hero-wholesale' && (
                  <Check className="w-6 h-6 text-cyan-400" />
                )}
              </label>

              {/* Placeholder for future providers */}
              <div className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-800 opacity-50">
                <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center shrink-0">
                  <Cloud className="w-6 h-6 text-slate-500" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-500">More providers coming soon</div>
                  <div className="text-sm text-slate-600">2Talk, and others</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Your SIP Credentials</h2>
              <p className="text-slate-400">Enter your phone extension credentials from Hero</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">SIP Username (Phone Number)</Label>
                <Input
                  placeholder="e.g., 830200010005"
                  value={form.sip_username}
                  onChange={(e) => setForm({ ...form, sip_username: e.target.value })}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 font-mono"
                />
                <p className="text-xs text-slate-500">Your extension number from the Hero portal</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">SIP Password</Label>
                <Input
                  type="password"
                  placeholder="Your SIP password"
                  value={form.sip_password}
                  onChange={(e) => setForm({ ...form, sip_password: e.target.value })}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                />
                <p className="text-xs text-slate-500">Found in Hero portal under Extensions → Your Extension → SIP Details</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Account Number</Label>
                <Input
                  placeholder="e.g., 8302000100"
                  value={form.account_number}
                  onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 font-mono"
                />
                <p className="text-xs text-slate-500">Your Hero account number. Team members will use this to join your company.</p>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">API Integration</h2>
              <p className="text-slate-400">Connect to your Hero portal for Click-to-Call and CDR sync</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">API Base URL</Label>
                <Select
                  value={form.api_base_url}
                  onValueChange={(value) => setForm({ ...form, api_base_url: value })}
                >
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="https://admin1.connect-us.co.nz">admin1.connect-us.co.nz</SelectItem>
                    <SelectItem value="https://admin2.connect-us.co.nz">admin2.connect-us.co.nz</SelectItem>
                    <SelectItem value="https://admin3.connect-us.co.nz">admin3.connect-us.co.nz</SelectItem>
                    <SelectItem value="custom">Custom URL...</SelectItem>
                  </SelectContent>
                </Select>
                {form.api_base_url === 'custom' && (
                  <Input
                    placeholder="https://your-portal.example.com"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 mt-2"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">API Username</Label>
                <Input
                  placeholder="Your portal login email"
                  value={form.api_username}
                  onChange={(e) => setForm({ ...form, api_username: e.target.value })}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">API Password</Label>
                <Input
                  type="password"
                  placeholder="Your portal password"
                  value={form.api_password}
                  onChange={(e) => setForm({ ...form, api_password: e.target.value })}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-sm text-blue-300">
                These credentials enable Click-to-Call and call history sync. You can find them in your Connect-US portal.
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Company Details</h2>
              <p className="text-slate-400">Tell us about your company</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Company Name *</Label>
                <Input
                  placeholder="Your Company Ltd"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Contacts Export URL (Optional)</Label>
                <Input
                  placeholder="https://portal.hero.co.nz/contacts.php?account=XXXXX"
                  value={form.contacts_url}
                  onChange={(e) => setForm({ ...form, contacts_url: e.target.value })}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                />
                <p className="text-xs text-slate-500">
                  Optional: Import your Hero phonebook contacts. You can add this later in Company Settings.
                </p>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Webhook Configuration</h2>
              <p className="text-slate-400">Set up real-time call notifications</p>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-blue-300 mb-2">Setup Instructions</h4>
              <ol className="text-sm text-slate-300 space-y-1 list-decimal list-inside">
                <li>Log into your Connect-US portal</li>
                <li>Navigate to API Settings → Webhooks</li>
                <li>Create a new webhook using the URL and Secret below</li>
                <li>Select events: Call Ringing, Call Answered, Call Ended</li>
              </ol>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Webhook URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={webhookData.url}
                    readOnly
                    className="bg-slate-900/50 border-slate-600 text-white font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-slate-600 hover:bg-slate-700 shrink-0"
                    onClick={() => copyToClipboard(webhookData.url, 'url')}
                  >
                    {copied.url ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Secret Token</Label>
                <div className="flex gap-2">
                  <Input
                    value={webhookData.secret}
                    readOnly
                    className="bg-slate-900/50 border-slate-600 text-white font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-slate-600 hover:bg-slate-700 shrink-0"
                    onClick={() => copyToClipboard(webhookData.secret, 'secret')}
                  >
                    {copied.secret ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <a
              href="https://portal1.connect-us.co.nz/apidocs/webhooks/index.php"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Open Webhook Documentation
            </a>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <PhoneCall className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">CONNECT</span>
          </div>
          <div className="text-sm text-slate-400">
            Setting up: <span className="text-white">{companyData?.company_name || 'Your Company'}</span>
          </div>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-12">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex flex-col items-center ${index < STEPS.length - 1 ? 'flex-1' : ''}`}>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      currentStep > step.id
                        ? 'bg-emerald-500 text-white'
                        : currentStep === step.id
                        ? 'bg-cyan-500 text-white'
                        : 'bg-slate-800 text-slate-500'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-2 hidden sm:block ${
                    currentStep >= step.id ? 'text-slate-300' : 'text-slate-600'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-emerald-500' : 'bg-slate-800'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-8">
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-700">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>

                {currentStep < 5 ? (
                  <Button
                    onClick={handleNext}
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleComplete}
                    disabled={loading}
                    className="bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-400 hover:to-cyan-500 text-white"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Completing...
                      </>
                    ) : (
                      <>
                        Complete Setup
                        <Check className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
